import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Camera,
  RefreshCw,
  Check,
  Upload,
  AlertCircle,
  ArrowLeft,
  Trash2,
  Scan,
} from 'lucide-react';

interface EpiCameraCaptureProps {
  label: string;
  onPhotoCaptured?: (base64: string) => void;
  onCapture?: (base64: string) => void;
  initialPhotoUrl?: string | null;
  facingMode?: 'user' | 'environment';
  modeType?: 'biometria' | 'evidencia';
  required?: boolean;
}

export const EpiCameraCapture: React.FC<EpiCameraCaptureProps> = ({
  label,
  onPhotoCaptured,
  onCapture,
  initialPhotoUrl = null,
  facingMode = 'user',
  modeType = 'evidencia',
  required = false,
}) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(initialPhotoUrl);
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  const [tempCapturedPhoto, setTempCapturedPhoto] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [currentFacingMode, setCurrentFacingMode] = useState<'user' | 'environment'>(facingMode);
  const [isStartingCamera, setIsStartingCamera] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Sincronizar initialPhotoUrl caso mude externamente
  useEffect(() => {
    if (initialPhotoUrl !== undefined) {
      setPhotoPreview(initialPhotoUrl);
    }
  }, [initialPhotoUrl]);

  // Função para parar todos os tracks da mídia
  const stopCameraStream = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  // Iniciar a câmera com o facingMode desejado
  const startCameraStream = useCallback(
    async (mode: 'user' | 'environment') => {
      setCameraError(null);
      setIsStartingCamera(true);
      try {
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }

        const constraints: MediaStreamConstraints = {
          video: {
            facingMode: mode,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        };

        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        setStream(mediaStream);
        setCurrentFacingMode(mode);
        setIsCameraActive(true);
      } catch (err: unknown) {
        console.warn('Erro ao acessar câmera com restrições ideais, tentando genérico:', err);
        try {
          // Tentativa com restrição genérica de vídeo
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
          setStream(fallbackStream);
          setIsCameraActive(true);
        } catch (fallbackErr: unknown) {
          console.error('Falha completa ao abrir câmera:', fallbackErr);
          setCameraError(
            'Não foi possível acessar a câmera. Verifique as permissões do navegador ou utilize o upload de imagem.',
          );
          setIsCameraActive(false);
        }
      } finally {
        setIsStartingCamera(false);
      }
    },
    [stream],
  );

  // Conectar o stream de vídeo logo que o modal/elemento da câmera for renderizado no DOM
  useEffect(() => {
    if (isCameraActive && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current
        .play()
        .then(() => {
          // Vídeo em reprodução ativa com sucesso
        })
        .catch((playErr) => {
          console.warn('Erro ao reproduzir stream de vídeo:', playErr);
        });
    }
  }, [isCameraActive, stream]);

  // Limpeza ao desmontar componente
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  // Abrir o visor de câmera
  const handleOpenLiveCamera = () => {
    setTempCapturedPhoto(null);
    startCameraStream(currentFacingMode);
  };

  // Alternar entre câmera frontal e traseira
  const handleToggleFacingMode = () => {
    const nextMode = currentFacingMode === 'user' ? 'environment' : 'user';
    startCameraStream(nextMode);
  };

  // Capturar frame da foto para conferência
  const handleSnapPhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;

    const canvas = canvasRef.current || document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Se estiver em modo selfie frontal, espelhar para visualização natural
    if (currentFacingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

    setTempCapturedPhoto(dataUrl);
  };

  // Confirmar foto tirada e fechar visor
  const handleConfirmCapturedPhoto = () => {
    if (!tempCapturedPhoto) return;
    setPhotoPreview(tempCapturedPhoto);
    onPhotoCaptured?.(tempCapturedPhoto);
    onCapture?.(tempCapturedPhoto);
    stopCameraStream();
    setIsCameraActive(false);
    setTempCapturedPhoto(null);
  };

  // Descartar captura temporária e voltar a ver a câmera ao vivo
  const handleRetakeInViewfinder = () => {
    setTempCapturedPhoto(null);
  };

  // Cancelar captura e voltar sem salvar
  const handleCancelCamera = () => {
    stopCameraStream();
    setIsCameraActive(false);
    setTempCapturedPhoto(null);
  };

  // Carregar imagem de arquivo
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setPhotoPreview(dataUrl);
      onPhotoCaptured?.(dataUrl);
      onCapture?.(dataUrl);
      stopCameraStream();
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Remover foto atual
  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    onPhotoCaptured?.('');
    onCapture?.('');
  };

  const isBiometric = modeType === 'biometria' || currentFacingMode === 'user';

  return (
    <div className="w-full flex flex-col">
      {/* Label com indicador de obrigatoriedade */}
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-bold text-gray-800 tracking-tight flex items-center gap-1">
          <span>{label}</span>
          {required && <span className="text-rose-500 font-bold">*</span>}
        </label>
        {photoPreview && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
            <Check className="w-3 h-3 text-emerald-600" /> Foto anexada
          </span>
        )}
      </div>

      {/* ============================================================== */}
      {/* CARD DO FORMULÁRIO (QUANDO A CÂMERA AO VIVO NÃO ESTÁ ABERTA)   */}
      {/* ============================================================== */}
      {photoPreview ? (
        <div className="relative group w-full rounded-2xl overflow-hidden border border-emerald-200 bg-gray-950 shadow-sm transition-all hover:shadow-md">
          <img
            src={photoPreview}
            alt={label}
            className="w-full h-44 object-cover object-center group-hover:scale-[1.02] transition-transform duration-300"
          />
          {/* Overlay de ações rápidas */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-3">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={handleOpenLiveCamera}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 hover:bg-white text-gray-900 rounded-xl text-xs font-bold shadow-sm backdrop-blur-xs transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5 text-emerald-600" />
                <span>Tirar Outra</span>
              </button>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  title="Substituir por arquivo"
                  className="p-1.5 bg-white/90 hover:bg-white text-gray-700 rounded-xl text-xs shadow-sm transition-colors"
                >
                  <Upload className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  title="Remover foto"
                  className="p-1.5 bg-rose-600/90 hover:bg-rose-600 text-white rounded-xl text-xs shadow-sm transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full rounded-2xl border-2 border-dashed border-gray-300 hover:border-emerald-400 bg-slate-50/70 hover:bg-emerald-50/20 p-4 transition-all flex flex-col items-center justify-center text-center group">
          <div className="w-12 h-12 rounded-2xl bg-white border border-gray-200 group-hover:border-emerald-200 group-hover:bg-emerald-50 text-gray-600 group-hover:text-emerald-700 flex items-center justify-center mb-2.5 shadow-2xs transition-all">
            {isBiometric ? <Scan className="w-6 h-6" /> : <Camera className="w-6 h-6" />}
          </div>

          <p className="text-xs font-medium text-gray-700 mb-1">
            {isBiometric ? 'Capture a biometria facial frontal' : 'Fotografe a evidência de entrega dos EPIs'}
          </p>
          <p className="text-[11px] text-gray-500 mb-3.5">
            Abra a câmera do dispositivo em tela cheia ou envie um arquivo
          </p>

          <div className="flex items-center gap-2 w-full max-w-xs">
            <button
              type="button"
              onClick={handleOpenLiveCamera}
              disabled={isStartingCamera}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-xs hover:shadow transition-all active:scale-[0.98] disabled:opacity-50"
            >
              <Camera className="w-4 h-4" />
              <span>{isStartingCamera ? 'Iniciando...' : 'Abrir Câmera'}</span>
            </button>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-semibold shadow-2xs transition-colors"
            >
              <Upload className="w-3.5 h-3.5" />
              <span>Arquivo</span>
            </button>
          </div>

          {cameraError && (
            <div className="mt-3 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-xl p-2.5 flex items-center gap-2 text-left w-full">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{cameraError}</span>
            </div>
          )}
        </div>
      )}

      {/* Input de arquivo invisível */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* ============================================================== */}
      {/* VISOR DE CÂMERA EM TELA CHEIA / OVERLAY COMPLETO DO MODAL     */}
      {/* ============================================================== */}
      {isCameraActive && (
        <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex flex-col justify-between p-4 sm:p-6 animate-in fade-in duration-200">
          {/* Header Superior do Visor */}
          <div className="flex items-center justify-between text-white z-10">
            <button
              type="button"
              onClick={handleCancelCamera}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold backdrop-blur-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Voltar</span>
            </button>

            <div className="text-center">
              <h4 className="text-sm font-bold text-white tracking-wide">{label}</h4>
              <p className="text-[11px] text-white/70">
                {tempCapturedPhoto
                  ? 'Confira a imagem antes de confirmar'
                  : isBiometric
                  ? 'Posicione o rosto centralizado'
                  : 'Enquadre os itens de entrega'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {!tempCapturedPhoto && (
                <button
                  type="button"
                  onClick={handleToggleFacingMode}
                  title="Inverter Câmera (Frontal / Traseira)"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold backdrop-blur-sm transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Inverter</span>
                </button>
              )}
            </div>
          </div>

          {/* Área Central de Visualização / Enquadramento */}
          <div className="relative flex-1 flex items-center justify-center my-4 overflow-hidden rounded-2xl bg-black/50 border border-white/10">
            {tempCapturedPhoto ? (
              // Foto capturada pronta para conferência
              <div className="relative max-h-[70vh] w-full flex items-center justify-center">
                <img
                  src={tempCapturedPhoto}
                  alt="Pré-visualização"
                  className="max-h-[70vh] max-w-full rounded-2xl object-contain shadow-2xl border border-white/20"
                />
                <div className="absolute top-4 left-4 bg-emerald-500/90 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1.5 backdrop-blur-xs">
                  <Check className="w-3.5 h-3.5" /> Foto capturada com sucesso
                </div>
              </div>
            ) : (
              // Stream de Vídeo ao Vivo
              <div className="relative w-full h-full max-h-[72vh] flex items-center justify-center overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  onLoadedMetadata={() => {
                    videoRef.current?.play().catch(console.warn);
                  }}
                  className={`max-h-[72vh] w-full object-cover rounded-2xl ${
                    currentFacingMode === 'user' ? 'scale-x-[-1]' : ''
                  }`}
                />
                <canvas ref={canvasRef} className="hidden" />

                {/* HUD e Marcadores de Enquadramento */}
                <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center p-6">
                  {isBiometric ? (
                    // Moldura Oval para Biometria Facial
                    <div className="relative w-64 h-80 sm:w-72 sm:h-96 rounded-[50%] border-2 border-dashed border-emerald-400/80 shadow-[0_0_80px_rgba(16,185,129,0.25)] flex items-center justify-center">
                      <div className="absolute top-4 text-center px-3 py-1 bg-black/60 rounded-full text-[10px] text-emerald-300 font-bold backdrop-blur-xs">
                        Alinhe os olhos e a face aqui
                      </div>
                    </div>
                  ) : (
                    // Moldura Retangular para Evidência de Entrega
                    <div className="relative w-full max-w-md h-72 sm:h-84 rounded-2xl border-2 border-dashed border-emerald-400/80 shadow-[0_0_80px_rgba(16,185,129,0.2)] flex items-center justify-center">
                      <div className="absolute top-4 text-center px-3 py-1 bg-black/60 rounded-full text-[10px] text-emerald-300 font-bold backdrop-blur-xs">
                        Enquadre os EPIs e o recebedor
                      </div>
                      {/* Cantoneiras HUD */}
                      <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-emerald-400 rounded-tl-lg" />
                      <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-emerald-400 rounded-tr-lg" />
                      <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-emerald-400 rounded-bl-lg" />
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-emerald-400 rounded-br-lg" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer Inferior com Botões de Ação */}
          <div className="flex items-center justify-center gap-4 z-10 py-2">
            {tempCapturedPhoto ? (
              // Ações de Revisão
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleRetakeInViewfinder}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-white/15 hover:bg-white/25 text-white text-xs font-bold backdrop-blur-md transition-all active:scale-95"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Tirar Outra Foto</span>
                </button>

                <button
                  type="button"
                  onClick={handleConfirmCapturedPhoto}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold shadow-lg shadow-emerald-500/30 transition-all active:scale-95"
                >
                  <Check className="w-4 h-4" />
                  <span>Confirmar e Usar Foto</span>
                </button>
              </div>
            ) : (
              // Botão Disparador da Câmera
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleSnapPhoto}
                  className="group relative flex items-center justify-center w-20 h-20 rounded-full border-4 border-white bg-white/20 backdrop-blur-xs hover:scale-105 active:scale-95 transition-all shadow-2xl"
                  title="Capturar Foto"
                >
                  <div className="w-14 h-14 rounded-full bg-emerald-500 group-hover:bg-emerald-400 transition-colors flex items-center justify-center shadow-md">
                    <Camera className="w-7 h-7 text-white" />
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
